import { buildApiRequestHeaders, forwardCookiesToClient, navigateTo, useApm, useCoreConfig, useCoreLogger, useRequestEvent, useRequestHeaders, useRequestURL } from '#imports'
import type { FetchError, FetchOptions, FetchContext, ResponseType } from 'ofetch'
import { createFetchError, $fetch } from 'ofetch'
import { joinURL } from 'ufo'
import defu from 'defu'
import { once } from 'es-toolkit'
import type { UseApiOptions } from '../../types'

// Error helpers
function useApiErrorHandler() {
    const apm = useApm()
    const logger = useCoreLogger()

    function createError(context: FetchContext): FetchError {
        if (!context.error) {
            // Use message from response data, if present
            // We only manually set an error when the request is a monolith request.
            const errorMessage = context.response?._data?.message || 'Monolith request failed. Success is false.'
            context.error = new Error(errorMessage)
        }

        return createFetchError(context)
    }

    function report(context: FetchContext) {
        const responseBody = typeof context.response?._data === 'string' ? context.response._data : JSON.stringify(context.response?._data)
        context.error = new Error(responseBody)
        const error = createFetchError(context)

        if (import.meta.server) {
            logger.withTag('useApiErrorHandler/report').error(error)
        }

        if (import.meta.client) {
            if (!apm) return
            apm.captureError(error)
        }
    }

    return {
        createError,
        report,
    }
}

// Guards
export function checkResponse(context: FetchContext, errorHandler: ReturnType<typeof useApiErrorHandler>) {
    if (context.response?._data?.success === false) {
        const error = errorHandler.createError(context)
        errorHandler.report({ ...context, error })
        throw error
    }

    return true
}
// End guards

const useApiOptions = <R extends ResponseType = ResponseType>(options: UseApiOptions = {}) => {
    const { enableLegacySupport = false, enableCookieForwarding = true, service, ...fetchOptions } = options

    const config = useCoreConfig()
    const event = useRequestEvent()
    const url = useRequestURL()
    const requestHeaders = useRequestHeaders()
    const apiErrorHandler = useApiErrorHandler()
    const getLogger = once(() => useCoreLogger().withTag('useApiOptions'))

    const { onRequest, onResponse, ...filteredFetchOptions } = fetchOptions

    let baseURL = filteredFetchOptions.baseURL

    if (import.meta.server) {
        const serviceConfig = service && config.services?.[service]?.server

        if (serviceConfig?.baseUrl) {
            baseURL = joinURL(serviceConfig.baseUrl, baseURL || '')
        }
    }

    if (!baseURL) {
        baseURL = `${useRequestURL().origin}`
    }

    const preparedOptions = defu<FetchOptions<R>, FetchOptions<R>[]>(
        {
            baseURL,
            credentials: 'include',

            async onRequest(context) {
                if (import.meta.server) {
                    const url = (context.request as Request)?.url || context.request as string

                    let isOutsideNebula = false

                    if (url.startsWith('/')) {
                        if (!baseURL.includes('cego-nebula-http-proxy')) {
                            isOutsideNebula = true
                        }
                    }
                    else if (!url.includes('cego-nebula-http-proxy')) {
                        isOutsideNebula = true
                    }

                    if (isOutsideNebula) {
                        getLogger().warn({
                            message: `Request made outside of nebula. Url is: ${url}. baseURL is: ${baseURL}`,
                            isOutsideNebula
                        })
                    }
                }

                const serviceConfig = service && config.services?.[service]

                context.options.headers = buildApiRequestHeaders({
                    event: event!,
                    requestHeaders,
                    extraHeaders: {
                        ...fetchOptions.headers || {},
                        ...Object.fromEntries(context.options.headers || {}),
                    },
                    service: serviceConfig
                })

                if (onRequest) {
                    if (Array.isArray(onRequest)) {
                        return Promise.allSettled(onRequest.map(callback => callback(context)))
                    }
                    return onRequest(context)
                }
            },

            onRequestError(context) {
                apiErrorHandler.report(context)
            },

            async onResponse(context) {
                if (context.response.ok) {
                    if (enableLegacySupport) {
                        checkResponse(context, apiErrorHandler)
                    }
                }

                if (import.meta.server) {
                    if (enableCookieForwarding) {
                        forwardCookiesToClient(event!, context.response.headers)
                    }
                }

                if (onResponse) {
                    if (Array.isArray(onResponse)) {
                        return Promise.allSettled(onResponse.map(callback => callback(context)))
                    }
                    return onResponse(context)
                }
            },
            async onResponseError(context) {
                apiErrorHandler.report(context)

                if (import.meta.client && context.response?.status === 401) {
                    await navigateTo({
                        path: config.site?.frontpageUrl || '/',
                        query: {
                            returnUrl: url.pathname,
                        },
                    })
                }
            },
        },
        filteredFetchOptions as FetchOptions<R>
    )

    return preparedOptions
}

export const useApi = (options: UseApiOptions = {}) => {
    const apiOptions = useApiOptions(options)

    const instance = $fetch.create(apiOptions)

    return instance
}
